import type { ReactNode } from 'react';

import { Badge } from '@monksflow/monks-ui';

interface McpServerCardProps {
  name: string;
  description?: string | null;
  /** Small label chip shown next to the name (e.g. "Built-in" for platform servers). */
  tag?: string;
  /** Simple text status (rendered as a badge). Ignored when `statusContent` is provided. */
  statusLabel?: string;
  /** Rich status slot (e.g. a live health badge); takes precedence over `statusLabel`. */
  statusContent?: ReactNode;
  /** Composed by the caller; omit for read-only cards (e.g. built-in platform servers). */
  actions?: ReactNode;
}

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

/** Presentational marketplace card for a single MCP server. Actions are composed by the caller. */
export const McpServerCard = ({
  name,
  description,
  tag,
  statusLabel,
  statusContent,
  actions,
}: McpServerCardProps) => (
  <div className="bg-card border-border flex h-full flex-col gap-3 rounded-xl border p-5">
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-3">
        <div className="bg-primary/10 text-primary text-body-sm flex size-10 shrink-0 items-center justify-center rounded-md font-semibold">
          {initialsOf(name)}
        </div>
        <div className="flex min-w-0 items-center gap-3">
          <span className="text-body-lg truncate font-medium" title={name}>
            {name}
          </span>
          {tag && <Badge variant="secondary">{tag}</Badge>}
        </div>
      </div>
      {statusContent ?? (statusLabel && <Badge variant="secondary">{statusLabel}</Badge>)}
    </div>

    {description && (
      <p className="text-body-sm text-muted-foreground line-clamp-2">{description}</p>
    )}

    {actions && <div className="mt-auto flex justify-end gap-2 pt-2">{actions}</div>}
  </div>
);
